import React, { useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const VIDEO_URL = process.env.REACT_APP_DEMO_VIDEO_URL || 'https://promotly-media.fra1.cdn.digitaloceanspaces.com/videos/571a5437-0bf0-4fef-bd6e-ffd0433c0855.mp4';
const THUMBNAIL_PATH = '/assets/images/landing/video-thumbnail.jpg';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.25, ease: 'easeOut' } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2, ease: 'easeIn' } },
};

const VideoDemoModal = ({ isOpen, onClose }) => {
  const videoRef = useRef(null);

  const handleEsc = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      if (videoRef.current) {
        videoRef.current.play().catch(() => {});
      }
    }
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, handleEsc]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={{ duration: 0.2 }}
          dir="ltr"
        >
          <div
            className="absolute inset-0 bg-black/70"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            className="relative w-full max-w-5xl mx-auto bg-black rounded-lg overflow-hidden shadow-2xl"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Promotly demo video"
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-50 flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors duration-200"
              aria-label="Close video"
              style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
              <video
                ref={videoRef}
                src={VIDEO_URL}
                poster={THUMBNAIL_PATH}
                controls
                preload="auto"
                playsInline
                autoPlay
                className="absolute inset-0 w-full h-full"
                style={{ objectFit: 'contain' }}
              />
              <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-black/60 text-white text-xs sm:text-sm font-medium pointer-events-none">
                30s Demo
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoDemoModal;
